import { fetchAuthSession, getCurrentUser } from "@aws-amplify/auth";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { signOut } from "../lib/services/authService";
import { backendApiService } from "../lib/services/backendApiService";
import { SubscriptionStatus } from "../lib/types/subscription";
import { validateSession } from "../lib/utils/cognitoTokenValidator";

interface User {
  email: string;
  username: string;
  emailVerified?: boolean;
  subscription?: SubscriptionStatus | null;

  name?: string | null | undefined;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  refreshAuth: () => Promise<void>;
  hasActiveSubscription: boolean;
  subscriptionError: string | null;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null,
  );

  const fetchSubscription = async (currentUser: User | null) => {
    if (!currentUser) return;
    try {
      setSubscriptionError(null);
      const sub = await backendApiService.fetchSubscriptionStatus();
      setUser((prev) =>
        prev
          ? { ...prev, subscription: sub }
          : { ...currentUser, subscription: sub },
      );
    } catch (err) {
      // Non-fatal: don't break auth flow if subscription fetch fails
      console.warn("Failed to fetch subscription status:", err);
      setSubscriptionError(
        "Unable to verify subscription status. Please check your network connection.",
      );
    }
  };

  const checkAuth = async () => {
    try {
      // Force refresh to get latest Cognito attributes
      const session = await fetchAuthSession({ forceRefresh: true });

      // Check if session has tokens and they are valid
      const hasValidSession =
        session.tokens?.accessToken &&
        session.tokens?.idToken &&
        validateSession(session.tokens.accessToken, session.tokens.idToken);

      if (hasValidSession) {
        const currentUser = await getCurrentUser();
        const baseUser: User = {
          email: currentUser.signInDetails?.loginId || "",
          username: currentUser.username || "",
          emailVerified: !!session.tokens?.idToken?.payload?.email_verified,
          name: session.tokens?.idToken?.payload?.name
            ? String(session.tokens.idToken.payload.name)
            : null,
        };
        setUser(baseUser);
        // Fetch subscription status in background - don't block auth completion
        fetchSubscription(baseUser).catch((err) => {
          console.warn("Background subscription fetch failed:", err);
        });
      } else {
        // Clear tokens if any are invalid or missing
        console.warn("Invalid session detected, clearing tokens");
        setUser(null);
      }
    } catch (error) {
      console.warn("Auth check failed:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const { signIn } = await import("../lib/services/authService");
    try {
      setUser(null);

      const result = await signIn(email, password);
      if (result) {
        await checkAuth();
      }
    } catch (error: any) {
      // If still getting "already authenticated" error, force sign out and retry
      if (
        error.name === "UserAlreadyAuthenticatedException" ||
        error.message?.includes("already authenticated")
      ) {
        console.log("Forcing sign out and retry...");
        await signOut();
        setUser(null);

        // Wait a moment then retry
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const result = await signIn(email, password);
        if (result) {
          await checkAuth();
        }
      } else {
        throw error;
      }
    }
  };

  const logout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.warn("Normal logout failed, forcing sign out:", error);
      // Use force sign out if normal logout fails
      await signOut();
    }
    setUser(null);
  };

  const refreshSubscription = async () => {
    if (!user) return;
    try {
      setSubscriptionError(null);
      const sub = await backendApiService.fetchSubscriptionStatus();
      setUser((prev) => (prev ? { ...prev, subscription: sub } : prev));
    } catch (err) {
      console.warn("refreshSubscription failed:", err);
      setSubscriptionError(
        "Unable to verify subscription status. Please check your network connection.",
      );
    }
  };

  const refreshAuth = async () => {
    // Re-run auth check to refresh tokens / user attributes and subscription
    setIsLoading(true);
    try {
      await checkAuth();
    } catch (err) {
      console.warn("refreshAuth failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const hasActiveSubscription = !!user?.subscription?.has_active_subscription;

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    setUser,
    refreshAuth,
    hasActiveSubscription,
    subscriptionError,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
